import { motion } from "framer-motion"
import { useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDisableTmaSwipes } from "@/shared/hooks/use-disable-tma-swipes"
import { TRANSITION } from "@/shared/lib/motion"
import { useTour } from "../model/tour-context"
import { useTourTarget } from "../model/use-tour-target"
import { TourTooltip } from "./tour-tooltip"

const CUTOUT_PADDING = 8
const TOOLTIP_GAP = 16
const SCREEN_MARGIN = 16
const FALLBACK_TOOLTIP_HEIGHT = 190

export function TourOverlay() {
	const { isOpen, step, stepIndex, totalSteps, isFirst, isLast, next, back, skipMissing, skip } =
		useTour()
	const target = useTourTarget(isOpen ? (step?.target ?? null) : null, skipMissing)
	const tooltipRef = useRef<HTMLDivElement>(null)
	const [tooltipHeight, setTooltipHeight] = useState(FALLBACK_TOOLTIP_HEIGHT)

	useDisableTmaSwipes()

	// biome-ignore lint/correctness/useExhaustiveDependencies: шаг это триггер пересчёта, а не данные эффекта
	useLayoutEffect(() => {
		const height = tooltipRef.current?.offsetHeight
		if (height) setTooltipHeight(height)
	}, [stepIndex, isOpen])

	if (!isOpen || !step || !target) return null

	const { rect, radius } = target
	const cutoutTop = rect.top - CUTOUT_PADDING
	const cutoutLeft = rect.left - CUTOUT_PADDING
	const cutoutWidth = rect.width + CUTOUT_PADDING * 2
	const cutoutHeight = rect.height + CUTOUT_PADDING * 2
	const cutoutBottom = cutoutTop + cutoutHeight

	const fitsBelow = cutoutBottom + TOOLTIP_GAP + tooltipHeight < window.innerHeight - SCREEN_MARGIN
	const tooltipTop = fitsBelow
		? cutoutBottom + TOOLTIP_GAP
		: Math.max(SCREEN_MARGIN, cutoutTop - TOOLTIP_GAP - tooltipHeight)

	return createPortal(
		<div className="fixed inset-0 z-[100]">
			<motion.div
				layout
				className="fixed"
				style={{
					top: cutoutTop,
					left: cutoutLeft,
					width: cutoutWidth,
					height: cutoutHeight,
					borderRadius: `calc(${radius} + ${CUTOUT_PADDING}px)`,
					boxShadow: "0 0 0 9999px rgba(0,0,0,0.72)",
				}}
				transition={TRANSITION.spring}
			/>

			<TourTooltip
				ref={tooltipRef}
				step={step}
				stepIndex={stepIndex}
				totalSteps={totalSteps}
				isFirst={isFirst}
				isLast={isLast}
				top={tooltipTop}
				onNext={next}
				onBack={back}
				onSkip={skip}
			/>
		</div>,
		document.body,
	)
}
